import { useSignIn } from '@/hooks/SignIn/useSignIn';

import { GET_CART_DETAILS_QUERY } from '@/hooks/SignIn/signIn.gql';
import { isRequired } from '@/util/formValidators';
import { ErrorMessage } from '@hookform/error-message';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import FormError from '../FormError/formError';

const TrackOrderSignIn = props => {
  const { setDefaultUsername = () => {}, showCreateAccount = () => {}, showForgotPassword = () => {} } = props;

  const { register, handleSubmit, formState } = useForm({
    defaultValues: {
      email: '',
      password: '',
    },
  });
  const [passwordInputType, setPasswordInputType] = useState('password');

  const router = useRouter();

  const {
    errors,
    handleSubmit: handleSubmitSignIn,
    isBusy,
    loading,
  } = useSignIn({
    getCartDetailsQuery: GET_CART_DETAILS_QUERY,
    setDefaultUsername,
    showCreateAccount,
    afterSubmit: () => {
      router.push('/customer/account');
    },
    showForgotPassword,
  });

  return (
    <form
      className='mt-5'
      onSubmit={handleSubmit(({ email, password }) => {
        handleSubmitSignIn({
          values: {
            email,
            password,
          },
        });
      })}
    >
      <div>
        <div className='font-roboto-regular text-base leading-6'>
          <span className='text-e82f2f'>*</span>
          <span className='ml-0.5'>Email</span>
        </div>
      </div>
      <input
        {...register('email', {
          validate: isRequired,
        })}
        className='mt-[5px] h-[50px] w-full rounded-[3px] border-1 border-solid border-e0e0e0 px-5 font-roboto-light text-base outline-0'
      />
      <div className='relative'>
        <div className='absolute left-0 top-0 font-roboto-regular text-xs text-e82f2f'>
          <ErrorMessage
            errors={formState.errors}
            name='email'
          ></ErrorMessage>
        </div>
      </div>

      <div className='mt-5 '>
        <div className='flex items-center font-roboto-regular text-base leading-6'>
          <span className='text-e82f2f'>*</span>
          <span className='ml-0.5'>Password</span>
          <Link
            href='/forgot-password'
            className='ml-auto text-xs leading-none text-[#757575] underline'
          >
            Forgot Your Password?
          </Link>
        </div>
      </div>
      <div className='relative mt-[5px]'>
        <input
          {...register('password', {
            validate: isRequired,
          })}
          type={passwordInputType}
          className='h-[50px] w-full rounded-[3px] border-1 border-solid border-e0e0e0 pl-5 pr-[50px] font-roboto-light text-base outline-0'
        />

        <button
          type='button'
          className='absolute right-5 top-1/2 -translate-y-1/2'
          onClick={() => {
            setPasswordInputType(x => (x === 'text' ? 'password' : 'text'));
          }}
        >
          {passwordInputType === 'text' && eye}
          {passwordInputType === 'password' && eyesOff}
        </button>
      </div>
      <div className='relative'>
        <div className='absolute left-0 top-0 font-roboto-regular text-xs text-e82f2f'>
          <ErrorMessage
            errors={formState.errors}
            name='password'
          ></ErrorMessage>
        </div>
      </div>

      <button
        priority='high'
        className='mt-5 flex h-10 w-full items-center justify-center rounded-[102px] bg-black font-roboto-medium text-base text-white lg_h-[50px] lg_w-auto lg_px-[42px] lg_text-lg lg_enabled_hover_bg-4c4c4c'
        type='submit'
        disabled={isBusy || loading}
      >
        Sign In{loading && '...'}
      </button>
      <FormError errors={Array.from(errors.values())} />
    </form>
  );
};

const eyesOff = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='18'
    fill='none'
    viewBox='0 0 24 18'
  >
    <path
      fill='#4C4C4C'
      d='M6.203 12.366A12.9 12.9 0 0 1 2.518 9C3.381 7.808 6.555 4.016 12 4.016c.824 0 1.595.087 2.314.239l1.142-1.142A12.7 12.7 0 0 0 12 2.641C4.775 2.64 1.245 8.4 1.098 8.647a.69.69 0 0 0 0 .706c.089.15 1.431 2.335 4.09 4.028zM18.811 4.621l-1.014 1.014a12.9 12.9 0 0 1 3.685 3.367c-.863 1.192-4.037 4.984-9.482 4.984a11.2 11.2 0 0 1-2.314-.24L8.544 14.89c1.04.293 2.19.472 3.456.472 7.225 0 10.756-5.76 10.903-6.006a.69.69 0 0 0 0-.706c-.09-.15-1.432-2.335-4.092-4.028'
    ></path>
    <path
      fill='#4C4C4C'
      d='M12 13.124a4.125 4.125 0 0 0 3.867-5.562l-5.304 5.305c.448.166.932.257 1.437.257M7.875 9c0 .505.091.99.258 1.437l5.304-5.304A4.125 4.125 0 0 0 7.875 9M15.362 6.61l1.49-1.489 1.037-1.037L19.36 2.61a.688.688 0 0 0-.972-.972l-1.838 1.838-1.087 1.087-1.074 1.074-5.751 5.751-1.49 1.49-1.037 1.037-1.473 1.473a.687.687 0 1 0 .972.972l1.838-1.838 1.087-1.087 1.075-1.074z'
    ></path>
  </svg>
);

const eye = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='18'
    fill='none'
    viewBox='0 0 24 18'
  >
    <path
      fill='#4C4C4C'
      d='M12 15.36c7.224 0 10.756-5.762 10.902-6.007a.69.69 0 0 0 0-.706C22.756 8.402 19.224 2.64 12 2.64 4.775 2.64 1.244 8.4 1.098 8.647a.69.69 0 0 0 0 .706C1.244 9.598 4.775 15.36 12 15.36m0-11.344c5.445 0 8.619 3.792 9.482 4.984-.863 1.192-4.037 4.984-9.482 4.984-5.446 0-8.62-3.793-9.482-4.984C3.38 7.808 6.555 4.016 12 4.016'
    ></path>
    <path
      fill='#4C4C4C'
      d='M12 13.125a4.125 4.125 0 1 0 0-8.25 4.125 4.125 0 0 0 0 8.25m0-5.672a1.547 1.547 0 1 1 0 3.094 1.547 1.547 0 0 1 0-3.094'
    ></path>
  </svg>
);

export default TrackOrderSignIn;
